import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { secondaryColor } from '../../assets/jss/material-kit-react';

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    zIndex: 99999,
    pointerEvents: 'none',
  },
  filterContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
    minWidth: '600px',
    '@media (min-width: 960px)': {
      minWidth: '800px',
    },
    '& > *': {
      pointerEvents: 'auto',
    },
  },
  filterContent: {
    width: '100%',
    padding: '24px',
    borderRadius: '8px',
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    marginTop: '60px',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.mode === 'dark' ? '#666' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.mode === 'dark' ? '#888' : '#666',
      },
    },
  },
  filterButton: {
    position: 'relative',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
      border: `1px solid ${secondaryColor}`,
    },
    '&.open': {
      backgroundColor: theme.palette.secondary.main,
      border: `1px solid ${secondaryColor}`,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
        border: `1px solid ${secondaryColor}`,
      },
    },
    width: 56,
    height: 56,
    borderRadius: '50%',
    boxShadow:
      '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
    '& .MuiSvgIcon-root': {
      width: 24,
      height: 24,
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      border: `2px solid ${theme.palette.background.paper}`,
      minWidth: '20px',
      height: '20px',
      borderRadius: '10px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
    },
  },
  resetBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      border: `2px solid ${theme.palette.background.paper}`,
      minWidth: '20px',
      height: '20px',
      borderRadius: '10px',
      fontSize: '0.75rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4px',
      '&:hover': {
        backgroundColor: theme.palette.error.dark,
      },
    },
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.mode === 'dark' ? 'white' : 'black',
    marginBottom: '8px',
    padding: '0 16px 8px 16px',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
    textAlign: 'center',
    width: '100%',
  },
  columnDivider: {
    borderLeft: `1px solid ${secondaryColor}20`,
    height: '100%',
    margin: '0 8px',
  },
  headerImage: {
    width: '24px',
    height: '24px',
    objectFit: 'contain',
  },
  newItemsToggle: {
    '& .MuiToggleButton-root': {
      flex: 1,
      textTransform: 'none',
      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
      '&.Mui-selected': {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
  },
  savedSearchButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    position: 'relative',
    width: '100%',
    '& .count': {
      position: 'absolute',
      left: 16,
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    },
    '& .name': {
      width: '100%',
      textAlign: 'center',
    },
  },
  activeFilter: {
    border: `2px solid ${secondaryColor} !important`,
    '&:hover': {
      border: `2px solid ${secondaryColor} !important`,
    },
  },
  activeToggleButton: {
    '&.Mui-selected': {
      border: `2px solid ${secondaryColor} !important`,
      '&:hover': {
        border: `2px solid ${secondaryColor} !important`,
      },
    },
  },
  filterDivider: {
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
    margin: '8px 0',
    width: '100%',
  },
  filterSection: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    padding: '16px',
    width: '100%',
    minWidth: '320px',
    maxWidth: '900px',
    overflowX: 'auto',
    border: `1px solid ${secondaryColor}33`,
    borderRadius: '8px',
  },
  filterSectionD2RS: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '8px',
    padding: '16px',
    width: '100%',
    minWidth: '320px',
    maxWidth: '900px',
    overflowX: 'auto',
    border: `1px solid ${secondaryColor}33`,
    borderRadius: '8px',
    marginTop: '16px',
  },
  selectedFiltersContainer: {
    marginTop: 16,
    marginBottom: 16,
    padding: 16,
    border: `1px solid ${secondaryColor}33`,
    borderRadius: '8px',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
  },
  filterContentContainer: {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
    borderRadius: 1,
    border: `1px solid ${theme.palette.secondary.main}4D`,
    boxShadow:
      '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
    padding: 2,
    maxHeight: 'calc(100vh - 300px)',
    overflowY: 'auto',
    marginTop: '60px',
    width: '100%',
  },
  toolbarContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    backgroundColor: theme.palette.mode === 'dark' ? '#232323' : '#f5f5f5',
    borderRadius: '12px',
    padding: '8px 16px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#ddd'}`,
    width: '100%',
    justifyContent: 'flex-start',
  },
}));
